import { useState, useEffect, type ReactNode } from 'react';
import type { User, Session, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@parleyride/supabase';
import { AuthContext } from './AuthContext';
import { fetchUserIsDriver } from './useUserProfile';

/**
 * Narrow an unknown thrown value to a human-readable message.
 *
 * Deliberately NOT `@parleyride/hooks`'s `toErrorMessage`, which never returns
 * undefined: every caller here reads `getErrorMessage(e) || '<specific copy>'`,
 * so a generic 'Unexpected error' would win the `||` and suppress the tailored
 * message ('Invalid email or password', and the rest). The falsy return is the
 * contract. What both must share is the rule that matters — read `.message`
 * off the object rather than `String()`-ing the object itself, which is how
 * "[object Object]" reached users (see docs/FAILURE_PATTERNS.md #9).
 *
 * GoTrue answers with a real `AuthError extends Error`, so the first branch
 * takes almost every real failure; the object branch is the safety net.
 */
function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return undefined;
}

export type NotifyFn = (opts: {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
}) => void;

export interface AuthProviderProps {
  children: ReactNode;
  supabase: SupabaseClient<Database>;
  redirectOrigin: string;
  notify?: NotifyFn;
  onSignOut?: () => void;
  cleanupAuthState?: () => void;
}

export function AuthProvider({
  children,
  supabase,
  redirectOrigin,
  notify,
  onSignOut,
  cleanupAuthState,
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDriver, setIsDriver] = useState(false);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_IN' && currentSession?.user) {
        setTimeout(() => {
          fetchUserIsDriver(supabase, currentSession.user.id).then(setIsDriver);
        }, 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        fetchUserIsDriver(supabase, currentSession.user.id).then(setIsDriver);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [supabase]);

  const signUp = async (
    email: string,
    password: string,
    first_name: string,
    last_name: string,
  ) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${redirectOrigin}/`,
          data: { first_name, last_name },
        },
      });
      if (error) throw error;

      notify?.({
        title: 'Account created successfully',
        description:
          'Please check your email to confirm your account, then you can sign in.',
      });
    } catch (error) {
      console.error('Sign up failed:', error);
      notify?.({
        title: 'Registration failed',
        description:
          getErrorMessage(error) || 'Could not create your account. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      cleanupAuthState?.();

      try {
        const signOutPromise = supabase.auth.signOut({ scope: 'global' });
        await Promise.race([
          signOutPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Signout timeout')), 5000),
          ),
        ]);
      } catch {
        // Pre-signin cleanup; ignore errors.
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      notify?.({
        title: 'Signed in successfully',
        description: 'Welcome back to ParleyRide!',
      });
    } catch (error) {
      console.error('Sign in failed:', error);
      notify?.({
        title: 'Sign in failed',
        description: getErrorMessage(error) || 'Invalid email or password',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signInWithProvider = async (provider: 'google' | 'facebook') => {
    try {
      cleanupAuthState?.();
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch {
        // Ignore.
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${redirectOrigin}/` },
      });
      if (error) throw error;

      notify?.({
        title: `Redirecting to ${provider}`,
        description: 'Please complete the authentication process.',
      });
    } catch (error) {
      notify?.({
        title: 'Social login failed',
        description:
          getErrorMessage(error) || 'Could not sign in with social provider',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      cleanupAuthState?.();
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;

      notify?.({
        title: 'Signed out successfully',
        description: 'You have been logged out. See you again soon!',
      });

      onSignOut?.();
    } catch (error) {
      notify?.({
        title: 'Sign out failed',
        description: getErrorMessage(error) || 'Could not sign you out. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signInWithProvider,
        signOut,
        isDriver,
        setIsDriver,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
